import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@apollo/client";
import type { OnChangeFn, SortingState } from "@tanstack/react-table";
import Autocomplete from "@/components/Autocomplete";
import Breadcrumb from "@/components/Breadcrumb";
import DateSelect from "@/components/DateSelect";
import EmptyState from "@/components/EmptyState";
import Icon from "@/components/Icon";
import MultiSelect from "@/components/MultiSelect";
import PageHeading from "@/components/PageHeading/PageHeading";
import SearchInput from "@/components/SearchInput";
import Table, { TableControls, TableFooter } from "@/components/Table";
import PersonSideSheet from "@/features/person/components/PersonSideSheet";
import usePageTitle from "@/hooks/usePageTitle";
import usePagination from "@/hooks/usePagination";
import useTableRowMenu from "@/hooks/useTableRowMenu";
import { useParams, useSearchWithParams } from "@/hooks/useSearchParams";
import { useIsCompactLayout } from "@/hooks/useIsCompactLayout";
import AppLayout from "@/layouts/AppLayout";
import {
  ACHIEVEMENT_TYPES_QUERY,
  type AchievementTypesQueryResponse,
  type MembershipAchievementType
} from "@/pages/people/[pgaId]/queries";
import { ALL_SECTIONS_QUERY } from "@/pages/sections/queries";
import { useMembershipServicesNavLinks } from "@/pages/people/_membershipServicesNavLinks";
import type { Path } from "@/router";
import type { Section } from "@/types";
import ExpandedAchievementFields from "./_components/ExpandedAchievementFields";
import {
  buildRecognitionYearOptions,
  buildSectionLookup,
  findDefaultAchievementType,
  hasNestedAchievementFields,
  isDateRangeValue,
  mapAchievementReportRows,
  MEMBER_STATUS_FILTER_OPTIONS,
  parseDateRangeParam,
  serializeDateRangeParam,
  toDateParam
} from "./_utils";
import type { AchievementForMembersFiltersInput } from "./types";
import {
  ACHIEVEMENTS_FOR_MEMBERS_QUERY,
  type AchievementForMembersOrderByInput,
  type AchievementsForMembersQueryResponse,
  type AchievementsForMembersQueryVariables
} from "./queries";
import { ACHIEVEMENT_REPORT_SORT_FIELD_MAP, getColumns } from "./_TableColumns";

type FilterOption = {
  label: string;
  value: string;
};

function splitCommaParam(value: string): string[] {
  return value
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

function buildAchievementTypeOptions(achievementTypes: MembershipAchievementType[] = []): FilterOption[] {
  return achievementTypes.map((achievementType) => ({
    label: achievementType.title,
    value: achievementType.id
  }));
}

function filterOptionsByQuery(options: FilterOption[], query: string): FilterOption[] {
  const search = query.trim().toLowerCase();
  if (!search) return options;
  return options.filter((option) => option.label.toLowerCase().includes(search));
}

function renderSectionOption(option: FilterOption) {
  return (
    <div className="flex items-center">
      <div className="bg-sys-brand-secondary-container rounded-full p-2 mr-4 flex items-center justify-center">
        <Icon name="domain" size={24} />
      </div>
      <div className="text-body-large">{option.label}</div>
    </div>
  );
}

export default function AchievementsReportPage() {
  usePageTitle("/people/membership-services/achievements-report", "People | Achievements Report");
  const navLinks = useMembershipServicesNavLinks();
  const { isCompactLayout } = useIsCompactLayout();
  const { params, addSearchParam, deleteSearchParams, searchParams } = useParams();
  const achievementId = typeof params.achievementId === "string" ? params.achievementId : undefined;
  const { searchValue, debouncedSearch, setSearchValue, clearSearch } = useSearchWithParams({
    paramName: "search"
  });
  const statusParam = searchParams.get("status[]") ?? "";
  const sectionNameParam = searchParams.get("sectionName[]") ?? "";
  const recognizingEntityParam = searchParams.get("recognizingEntity[]") ?? "";
  const recognitionYearParam = searchParams.get("recognitionYear[]") ?? "";
  const recognizedAtParam = searchParams.get("recognizedAt") ?? "";
  const expiresOnParam = searchParams.get("expiresOn") ?? "";
  const selectedStatusValues = useMemo(() => splitCommaParam(statusParam), [statusParam]);
  const selectedSectionName = useMemo(() => splitCommaParam(sectionNameParam)[0], [sectionNameParam]);
  const selectedRecognizingEntity = useMemo(() => splitCommaParam(recognizingEntityParam)[0], [recognizingEntityParam]);
  const selectedRecognitionYears = useMemo(() => splitCommaParam(recognitionYearParam), [recognitionYearParam]);
  const recognizedAtRange = useMemo(() => parseDateRangeParam(recognizedAtParam), [recognizedAtParam]);
  const expiresOnRange = useMemo(() => parseDateRangeParam(expiresOnParam), [expiresOnParam]);
  const { Menu, setMenu } = useTableRowMenu();
  const { paginationVariables, createHeaderPagination, createFooterPagination, resetPagination } = usePagination();
  const [sorting, setSorting] = useState<SortingState>([]);
  const [achievementTypeQuery, setAchievementTypeQuery] = useState("");
  const [sectionNameQuery, setSectionNameQuery] = useState("");
  const [recognizingEntityQuery, setRecognizingEntityQuery] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [selectedPgaId, setSelectedPgaId] = useState<string | null>(null);
  const [isPersonSideSheetOpen, setIsPersonSideSheetOpen] = useState(false);

  useEffect(() => {
    const timeout = window.setTimeout(() => setDebouncedSearchTerm(achievementTypeQuery.trim()), 300);
    return () => window.clearTimeout(timeout);
  }, [achievementTypeQuery]);

  useEffect(() => {
    resetPagination();
  }, [
    achievementId,
    debouncedSearch,
    statusParam,
    sectionNameParam,
    recognizingEntityParam,
    recognitionYearParam,
    recognizedAtParam,
    expiresOnParam,
    resetPagination
  ]);

  const {
    data: achievementTypesData,
    loading: achievementTypesLoading,
    error: achievementTypesError
  } = useQuery<AchievementTypesQueryResponse>(ACHIEVEMENT_TYPES_QUERY, {
    variables: { search: debouncedSearchTerm || null },
    fetchPolicy: "cache-first"
  });

  const { data: sectionsData } = useQuery<{ sections: Section[] }>(ALL_SECTIONS_QUERY, {
    fetchPolicy: "cache-first"
  });

  const achievementTypeOptions = useMemo(
    () => buildAchievementTypeOptions(achievementTypesData?.achievementTypes),
    [achievementTypesData?.achievementTypes]
  );

  const defaultAchievementType = useMemo(
    () => findDefaultAchievementType(achievementTypesData?.achievementTypes),
    [achievementTypesData?.achievementTypes]
  );
  const hasAppliedDefaultType = useRef(!!achievementId);

  useEffect(() => {
    if (achievementId || hasAppliedDefaultType.current || !defaultAchievementType) {
      return;
    }

    hasAppliedDefaultType.current = true;
    addSearchParam("achievementId", defaultAchievementType.id);
    setAchievementTypeQuery("");
    setDebouncedSearchTerm("");
  }, [achievementId, addSearchParam, defaultAchievementType]);

  const selectedAchievementType = useMemo(
    () => achievementTypeOptions.find((option) => option.value === achievementId),
    [achievementId, achievementTypeOptions]
  );

  const orderBy = useMemo((): AchievementForMembersOrderByInput | undefined => {
    const sort = sorting[0];
    if (!sort) return undefined;

    const field = ACHIEVEMENT_REPORT_SORT_FIELD_MAP[sort.id];
    if (!field) return undefined;

    return {
      field,
      direction: sort.desc ? "DESC" : "ASC"
    };
  }, [sorting]);

  const handleSortingChange: OnChangeFn<SortingState> = (updaterOrValue) => {
    setSorting((currentSorting) =>
      typeof updaterOrValue === "function" ? updaterOrValue(currentSorting) : updaterOrValue
    );
    resetPagination();
  };

  const filters = useMemo((): AchievementForMembersFiltersInput | undefined => {
    const next: AchievementForMembersFiltersInput = {};
    if (debouncedSearch) next.search = debouncedSearch;
    if (selectedStatusValues.length > 0) next.status = selectedStatusValues;
    if (selectedSectionName) next.sectionName = [selectedSectionName];
    if (selectedRecognizingEntity) next.recognizingEntity = [selectedRecognizingEntity];
    if (selectedRecognitionYears.length > 0) next.recognitionYear = selectedRecognitionYears;
    if (recognizedAtRange) next.recognizedAt = recognizedAtRange;
    if (expiresOnRange) next.expiresOn = expiresOnRange;
    return Object.keys(next).length > 0 ? next : undefined;
  }, [
    debouncedSearch,
    expiresOnRange,
    recognizedAtRange,
    selectedRecognizingEntity,
    selectedRecognitionYears,
    selectedSectionName,
    selectedStatusValues
  ]);

  const {
    data: currentData,
    previousData,
    loading: achievementsLoading,
    error: achievementsError
  } = useQuery<AchievementsForMembersQueryResponse, AchievementsForMembersQueryVariables>(
    ACHIEVEMENTS_FOR_MEMBERS_QUERY,
    {
      variables: {
        achievementId: achievementId!,
        ...paginationVariables,
        ...(orderBy ? { orderBy } : {}),
        ...(filters ? { filters } : {})
      },
      skip: !achievementId
    }
  );

  const data = achievementId && !achievementsError ? currentData || previousData : undefined;
  const connection = data?.achievementsForMembers;
  const tableData = useMemo(() => mapAchievementReportRows(connection?.nodes ?? []), [connection?.nodes]);
  const totalRowCount = connection?.totalCount ?? tableData.length;
  const sectionLookup = useMemo(() => buildSectionLookup(sectionsData?.sections), [sectionsData?.sections]);

  const columns = useMemo(
    () =>
      getColumns({
        isCompactLayout,
        onViewPerson: (pgaId) => {
          setSelectedPgaId(pgaId);
          setIsPersonSideSheetOpen(true);
        },
        setMenu
      }),
    [isCompactLayout, setMenu]
  );

  const sectionOptions = useMemo(
    () =>
      (sectionsData?.sections ?? [])
        .map((section) => ({ value: section.name, label: section.name }))
        .sort((left, right) => left.label.localeCompare(right.label)),
    [sectionsData?.sections]
  );
  const recognitionYearOptions = useMemo(
    () => buildRecognitionYearOptions({ extraYears: selectedRecognitionYears }),
    [selectedRecognitionYears]
  );

  const recognizingEntityOptions = useMemo(
    () => filterOptionsByQuery(sectionOptions, recognizingEntityQuery),
    [recognizingEntityQuery, sectionOptions]
  );
  const selectedRecognizingEntityOption = useMemo(
    () => sectionOptions.find((option) => option.value === selectedRecognizingEntity),
    [sectionOptions, selectedRecognizingEntity]
  );
  const currentSectionOptions = useMemo(
    () => filterOptionsByQuery(sectionOptions, sectionNameQuery),
    [sectionNameQuery, sectionOptions]
  );
  const selectedSectionNameOption = useMemo(
    () => sectionOptions.find((option) => option.value === selectedSectionName),
    [sectionOptions, selectedSectionName]
  );

  const hasAchievementTypeSelected = !!achievementId;
  const hasListFilters =
    !!debouncedSearch ||
    selectedStatusValues.length > 0 ||
    !!selectedSectionName ||
    !!selectedRecognizingEntity ||
    selectedRecognitionYears.length > 0 ||
    !!recognizedAtRange ||
    !!expiresOnRange;
  const hasActiveFilters = !achievementTypesError || hasListFilters || hasAchievementTypeSelected;
  const activeFilterCount = [
    !!debouncedSearch,
    selectedStatusValues.length > 0,
    !!selectedSectionName,
    !!selectedRecognizingEntity,
    selectedRecognitionYears.length > 0,
    !!recognizedAtRange,
    !!expiresOnRange
  ].filter(Boolean).length;

  function setSelectedStatuses(statuses: string[]) {
    if (statuses.length > 0) {
      addSearchParam("status[]", statuses.join(","));
    } else {
      deleteSearchParams("status[]");
    }
    resetPagination();
  }

  function setSelectedSectionName(sectionName?: string) {
    if (sectionName) {
      addSearchParam("sectionName[]", sectionName);
    } else {
      deleteSearchParams("sectionName[]");
    }
    resetPagination();
  }

  function setSelectedRecognizingEntity(entityName?: string) {
    if (entityName) {
      addSearchParam("recognizingEntity[]", entityName);
    } else {
      deleteSearchParams("recognizingEntity[]");
    }
    resetPagination();
  }

  function setSelectedRecognitionYears(years: string[]) {
    if (years.length > 0) {
      addSearchParam("recognitionYear[]", years.join(","));
    } else {
      deleteSearchParams("recognitionYear[]");
    }
    resetPagination();
  }

  function setDateRangeParam(paramName: "recognizedAt" | "expiresOn", value: unknown) {
    if (!isDateRangeValue(value)) return;

    const from = toDateParam(value.startDate);
    const to = toDateParam(value.endDate);

    if (from || to) {
      addSearchParam(paramName, serializeDateRangeParam(from, to));
    } else {
      deleteSearchParams(paramName);
    }
    resetPagination();
  }

  function handleClearAllFilters() {
    clearSearch();
    setSectionNameQuery("");
    setRecognizingEntityQuery("");
    deleteSearchParams([
      "status[]",
      "sectionName[]",
      "recognizingEntity[]",
      "recognitionYear[]",
      "recognizedAt",
      "expiresOn"
    ]);
    resetPagination();
  }

  const currentSectionFilter = (
    <Autocomplete<FilterOption>
      label="Current Section"
      data={currentSectionOptions}
      query={selectedSectionNameOption?.label ?? sectionNameQuery}
      placeholder="Search Current Sections"
      data-testid="current-section-filter"
      leadingIcon="search"
      constrainMenuWidth
      menuClassName="max-h-[300px]"
      renderItem={renderSectionOption}
      onChangeText={(text) => {
        setSectionNameQuery(text);
        if (selectedSectionName) {
          deleteSearchParams("sectionName[]");
        }
      }}
      reset={() => {
        setSectionNameQuery("");
        setSelectedSectionName();
      }}
      onSelect={(option) => {
        setSelectedSectionName(option.value);
        setSectionNameQuery("");
      }}
    />
  );

  const recognizedByFilter = (
    <Autocomplete<FilterOption>
      label="Recognized By"
      data={recognizingEntityOptions}
      query={selectedRecognizingEntityOption?.label ?? recognizingEntityQuery}
      placeholder="Search Recognizing Entities"
      data-testid="recognized-by-filter"
      leadingIcon="search"
      constrainMenuWidth
      menuClassName="max-h-[300px]"
      renderItem={renderSectionOption}
      onChangeText={(text) => {
        setRecognizingEntityQuery(text);
        if (selectedRecognizingEntity) {
          deleteSearchParams("recognizingEntity[]");
        }
      }}
      reset={() => {
        setRecognizingEntityQuery("");
        setSelectedRecognizingEntity();
      }}
      onSelect={(option) => {
        setSelectedRecognizingEntity(option.value);
        setRecognizingEntityQuery("");
      }}
    />
  );

  const achievementTypeFilter = (
    <Autocomplete<FilterOption>
      variant="searchableDropdown"
      label="Achievement Type"
      required
      data={achievementTypeOptions}
      query={selectedAchievementType?.label ?? achievementTypeQuery}
      loadingResults={achievementTypesLoading}
      placeholder="Search Achievement Types"
      data-testid="achievement-type-filter"
      leadingIcon="search"
      constrainMenuWidth
      menuClassName="max-h-[300px]"
      renderItem={(option) => (
        <div className="flex flex-col">
          <div className="text-body-large">{option.label}</div>
        </div>
      )}
      onChangeText={(text) => {
        setAchievementTypeQuery(text);
        if (achievementId) {
          deleteSearchParams("achievementId");
        }
      }}
      reset={() => {
        setAchievementTypeQuery("");
        setDebouncedSearchTerm("");
        deleteSearchParams("achievementId");
      }}
      onSelect={(option) => {
        addSearchParam("achievementId", option.value);
        setAchievementTypeQuery("");
        setDebouncedSearchTerm("");
      }}
    />
  );

  return (
    <AppLayout interior bottomBar navLinks={navLinks}>
      <Breadcrumb
        title="Operations"
        compactTitle="Achievements Report"
        backLink={{ to: "/people/membership-services" }}
      />
      <PageHeading title="Achievements Report" compact />
      <div className="w-full pt-12">
        <Table
          data={tableData}
          columns={columns}
          loading={achievementsLoading && hasAchievementTypeSelected}
          hasActiveFilters={hasActiveFilters}
          serverSideSorting
          sortingState={sorting}
          onSortingChange={handleSortingChange}
          getRowLink={(row) => (row.original.pgaId ? { to: `/people/${row.original.pgaId}` as Path } : null)}
          getRowCanExpand={(row) => hasNestedAchievementFields(row.original)}
          renderExpandedRow={({ row }) => (
            <ExpandedAchievementFields row={row.original} sectionLookup={sectionLookup} />
          )}
          renderControls={(_table, { onSortClick } = {}) => (
            <TableControls
              pagination={createHeaderPagination(connection, {
                loading: achievementsLoading,
                pageCount: tableData.length,
                totalCount: totalRowCount
              })}
              onSortClick={onSortClick}
              useExpandedFilters
              expandedFiltersState={{
                status: selectedStatusValues.length > 0,
                sectionName: !!selectedSectionName,
                recognizingEntity: !!selectedRecognizingEntity,
                recognitionYear: selectedRecognitionYears.length > 0,
                recognizedAt: !!recognizedAtRange,
                expiresOn: !!expiresOnRange
              }}
              activeFilters={activeFilterCount}
              onClearAllFilters={handleClearAllFilters}
            >
              <SearchInput
                ariaLabel="Search Members"
                placeholder="Search Members"
                query={searchValue}
                onChange={(value) => {
                  setSearchValue(value);
                  resetPagination();
                }}
                onClear={() => {
                  clearSearch();
                  resetPagination();
                }}
              />
              <div data-name="achievementId" data-filter-size="custom" className="min-w-[280px]">
                {achievementTypeFilter}
              </div>
              <div data-name="status">
                <MultiSelect
                  label="Member Status"
                  selectedValues={selectedStatusValues}
                  options={MEMBER_STATUS_FILTER_OPTIONS}
                  className="w-full"
                  onSelect={(value, checked) => {
                    const statuses = checked
                      ? [...selectedStatusValues, value]
                      : selectedStatusValues.filter((status) => status !== value);
                    setSelectedStatuses(statuses);
                  }}
                  onClear={() => setSelectedStatuses([])}
                />
              </div>
              <div data-name="sectionName" data-filter-size="custom" className="min-w-[280px]">
                {currentSectionFilter}
              </div>
              <div data-name="recognizingEntity" data-filter-size="custom" className="min-w-[280px]">
                {recognizedByFilter}
              </div>
              <div data-name="recognitionYear">
                <MultiSelect
                  label="Recognition Year"
                  selectedValues={selectedRecognitionYears}
                  options={recognitionYearOptions}
                  className="w-full"
                  searchable
                  onSelect={(value, checked) => {
                    const years = checked
                      ? [...selectedRecognitionYears, value]
                      : selectedRecognitionYears.filter((year) => year !== value);
                    setSelectedRecognitionYears(years);
                  }}
                  onClear={() => setSelectedRecognitionYears([])}
                />
              </div>
              <div data-name="recognizedAt" data-filter-size="custom">
                <DateSelect
                  label="Recognized On"
                  placeholder="Show All"
                  value={{
                    startDate: recognizedAtRange?.from ?? null,
                    endDate: recognizedAtRange?.to ?? null
                  }}
                  startFrom={recognizedAtRange?.from ? new Date(`${recognizedAtRange.from}T00:00:00`) : undefined}
                  useModal
                  useDefaultShortcuts
                  popoverDirection="down"
                  onChange={(value) => setDateRangeParam("recognizedAt", value)}
                />
              </div>
              <div data-name="expiresOn" data-filter-size="custom">
                <DateSelect
                  label="Expires On"
                  placeholder="Show All"
                  value={{
                    startDate: expiresOnRange?.from ?? null,
                    endDate: expiresOnRange?.to ?? null
                  }}
                  startFrom={expiresOnRange?.from ? new Date(`${expiresOnRange.from}T00:00:00`) : undefined}
                  useModal
                  useDefaultShortcuts
                  popoverDirection="down"
                  onChange={(value) => setDateRangeParam("expiresOn", value)}
                />
              </div>
            </TableControls>
          )}
          renderFooter={() => (
            <TableFooter
              pagination={createFooterPagination(connection, {
                loading: achievementsLoading,
                pageCount: tableData.length,
                totalCount: totalRowCount
              })}
              totalRowCount={totalRowCount}
            />
          )}
          renderNoDataEmptyState={() => (
            <EmptyState
              title="Unable to Load Achievement Types"
              caption={achievementTypesError?.message ?? "Try again later."}
              iconName="error"
            />
          )}
          renderFilteredEmptyState={() =>
            achievementsError ? (
              <EmptyState
                title="Unable to Load Recognized Members"
                caption={achievementsError.message}
                iconName="error"
              />
            ) : hasAchievementTypeSelected && hasListFilters ? (
              <EmptyState title="No Results Found" caption="Try a different search or filter." iconName="search" />
            ) : hasAchievementTypeSelected ? (
              <EmptyState
                title="No Members Recognized"
                caption="There are no members recognized for this achievement."
                iconName="emoji_events"
              />
            ) : (
              <EmptyState
                title="Select an Achievement Type"
                caption="Choose an achievement type to view recognized members."
                iconName="emoji_events"
              />
            )
          }
          data-testid="achievements-report-table"
        />
      </div>

      <Menu />
      <PersonSideSheet
        open={isPersonSideSheetOpen}
        onClose={() => {
          setIsPersonSideSheetOpen(false);
          setSelectedPgaId(null);
        }}
        pgaId={selectedPgaId}
        title="Member Details"
      />
    </AppLayout>
  );
}
